import { type AnnouncementCommandAction } from './announcementCommandParams';
import {
  type AnnouncementActionType,
  type AnnouncementMediaType,
  type AnnouncementSurface,
  type AnnouncementTone,
  type AnnouncementVariant,
} from './vocabulary';

export type AnnouncementPlacement = {
  /**
   * A shorter version of the title for a shape with little room: it is all a collapsed pill shows.
   * Falls back to the title when empty.
   */
  shortTitle?: string;
  /**
   * Omitted when not set on the placement row or when the stored tone is not in `ANNOUNCEMENT_TONES`.
   * Falls back to the announcement's `style.tone` when omitted; an unknown tone is dropped, not guessed.
   */
  tone?: AnnouncementTone;
  variant: AnnouncementVariant;
};

export type AnnouncementUrlAction = {
  label: string;
  type: AnnouncementActionType;
  url: string;
};

export type AnnouncementAction = AnnouncementCommandAction | AnnouncementUrlAction;

export const isAnnouncementCommandAction = (action: AnnouncementAction): action is AnnouncementCommandAction => {
  return 'command' in action;
};

/**
 * One placement row as the API returns it. The surface and variant are strings here;
 * `narrowAnnouncementSettings` drops unknown values.
 */
export type AnnouncementPlacementPayload = {
  settings: { shortTitle?: string; tone?: string; variant: string };
  surface: string;
};

/**
 * Shared settings as the API returns them. Vocabularies are strings here;
 * `narrowAnnouncementSettings` drops unknown values.
 * Redeclared in this package so presentational code does not import the SDK.
 */
export type AnnouncementSettingsPayload = {
  actions: { command?: string; label: string; params?: Record<string, unknown>; type: string; url?: string }[];
  /**
   * Absent on announcements saved before media existed. `type` is absent on the Portal's save input, where the API
   * derives it from the key; `narrowAnnouncementSettings` drops items without a known type.
   */
  media?: { key: string; type?: string }[];
  style: { icon?: string; image?: string; tone: string };
};

/** An uploaded file, stored as its key in the marketing assets bucket. */
export type AnnouncementMediaItem = {
  key: string;
  type: AnnouncementMediaType;
};

/** A file ready to render: `src` is its imgix URL, already asking for a format the browser can play. */
export type AnnouncementMediaSource = {
  src: string;
  type: AnnouncementMediaType;
};

/** An announcement as `narrowAnnouncementSettings` reads it: shared settings plus placement rows. */
export type AnnouncementNarrowInput = {
  placements: readonly AnnouncementPlacementPayload[];
  settings: AnnouncementSettingsPayload;
};

export type AnnouncementStyle = {
  /** Any `@air/design-icons` export name, such as `MegaphoneIcon`. The host renders it. */
  icon?: string;
  image?: string;
  /**
   * Omitted when the stored tone is not in `ANNOUNCEMENT_TONES`.
   * An unknown tone is dropped, never replaced with a guess.
   */
  tone?: AnnouncementTone;
};

export type AnnouncementSettings = {
  actions: AnnouncementAction[];
  /** In the order the author arranged them. */
  media: AnnouncementMediaItem[];
  placements: Partial<Record<AnnouncementSurface, AnnouncementPlacement>>;
  style: AnnouncementStyle;
};
